<script lang="ts">
    import { onMount } from 'svelte';
    import { getPathParams } from 'svelte-mini-router';
    import { translate } from '@/utils/format';
    import { goTo } from '@/utils/router';
    import { Spinner } from '@/components';
    import { getSsoSession, setSsoSession, savePartnerProfile } from '@/services/sso';
    import { isTruthyEnv } from '@/services/referrals';

    type NutritionResult = {
        kcal: number;
        protein: number;
        hydration: number;
        insight: string;
        sportTip: string;
    };

    let partnerId = $state('');
    let loading = $state(true);
    let result = $state<NutritionResult | null>(null);
    let displayKcal = $state(0);
    let animationDone = $state(false);
    let saving = $state(false);

    function buildFakeResult(answers: Record<string, any>): NutritionResult {
        const goalMap: Record<string, number> = {
            '0': 2000, '1': 2100, '2': 1800, '3': 1650, '4': 2600, '5': 2800,
        };
        const freqMap: Record<string, number> = { low: -150, med: 0, high: 200 };

        const baseKcal = goalMap[answers?.goal ?? '1'] ?? 2100;
        const freqAdj = freqMap[answers?.freq ?? 'med'] ?? 0;
        const kcal = baseKcal + freqAdj;
        const protein = Math.round(kcal * 0.25 / 4);
        const hydration = +(1.8 + (answers?.freq === 'high' ? 0.6 : answers?.freq === 'med' ? 0.3 : 0)).toFixed(1);

        const sportInsights: Record<string, string> = {
            strength: 'La distribución proteica uniforme a lo largo del día maximiza la síntesis muscular.',
            run: 'Los carbohidratos antes del entrenamiento sostendrán tu rendimiento aeróbico.',
            hiit: 'La recuperación post-HIIT requiere carbohidratos de rápida absorción en los primeros 30 min.',
            cycling: 'El glucógeno muscular es tu principal combustible en sesiones de más de 60 min.',
            cardiovascular: 'Mantener una hidratación constante es clave para el rendimiento cardiovascular.',
            walk: 'Un déficit calórico moderado optimizará tu composición corporal con actividad moderada.',
            multifunctional: 'La variedad de movimientos requiere una distribución equilibrada de macronutrientes.',
        };

        const sportTip = sportInsights[answers?.sport ?? 'strength'] ?? sportInsights.strength;
        const insight = answers?.priority === 'muscle'
            ? 'Prioriza la proteína post-entreno para maximizar la hipertrofia.'
            : answers?.priority === 'energy'
            ? 'Distribuye los carbohidratos alrededor de tus sesiones para mantener la energía.'
            : 'Un balance adecuado de macros te ayudará a alcanzar tu objetivo.';

        return { kcal, protein, hydration, insight, sportTip };
    }

    function animateCounter(target: number) {
        const duration = 1800;
        const start = performance.now();
        const startValue = 0;

        function step(now: number) {
            const elapsed = now - start;
            const progress = Math.min(elapsed / duration, 1);
            const eased = 1 - Math.pow(1 - progress, 3);
            displayKcal = Math.round(startValue + (target - startValue) * eased);
            if (progress < 1) {
                requestAnimationFrame(step);
            } else {
                displayKcal = target;
                animationDone = true;
            }
        }

        requestAnimationFrame(step);
    }

    async function saveAndContinue() {
        saving = true;
        const session = getSsoSession();
        if (session?.webToken && result) {
            setSsoSession({ ...session, result });
            await savePartnerProfile(session.webToken, {
                status: 'profile_saved',
                nijiInitialInsight: result,
            }).catch(() => {/* non-blocking */});
        }
        saving = false;
        goTo(`/${partnerId}/companion`);
    }

    onMount(async () => {
        const params = getPathParams();
        partnerId = String(params?.['partnerId'] || '').trim();

        if (!partnerId) { goTo('/'); return; }

        const session = getSsoSession();

        if (isTruthyEnv(import.meta.env['VITE_FAKE_SSO']) || !session?.webToken) {
            await new Promise((r) => setTimeout(r, 800));
            result = buildFakeResult(session?.answers ?? {});
        } else {
            // Real mode: call backend endpoint for metabolic calculation
            // Fallback to client-side calculation if endpoint not available yet
            try {
                result = buildFakeResult(session?.answers ?? {});
            } catch {
                result = buildFakeResult({});
            }
        }

        loading = false;
        if (result) {
            setTimeout(() => animateCounter(result!.kcal), 300);
        }
    });
</script>

<section class="ssoResult">
    {#if loading}
        <div class="ssoResult__loading">
            <Spinner text="" />
            <p>{translate('SSO_CALCULATING', 'Calculando tu perfil...')}</p>
        </div>
    {:else if result}
        <!-- WOW moment -->
        <div class="ssoResult__wow">
            <div class="ssoResult__wowRing" class:ssoResult__wowRing--done={animationDone}>
                <span class="ssoResult__wowNum">{displayKcal.toLocaleString()}</span>
                <span class="ssoResult__wowUnit">{translate('NJO_WOW_UNIT', 'kcal/día')}</span>
            </div>
            <p class="ssoResult__wowLabel">{translate('NJO_WOW_LABEL', 'Tu necesidad calórica estimada')}</p>
        </div>

        <h2 class="ssoResult__title">{translate('NJO_RESULT_TITLE', 'Tu perfil nutricional')}</h2>

        <!-- Metric cards -->
        <div class="ssoResult__cards">
            <div class="ssoResult__card">
                <div class="ssoResult__cardIcon">🥩</div>
                <div class="ssoResult__cardBody">
                    <div class="ssoResult__cardTitle">{translate('NJO_RESULT_PROTEIN_TITLE', 'Proteína diaria')}</div>
                    <div class="ssoResult__cardValue">{result.protein}g</div>
                    <div class="ssoResult__cardDesc">
                        {translate('NJO_RESULT_PROTEIN_DESC', '{g}g · base para recuperación muscular', { g: String(result.protein) })}
                    </div>
                </div>
            </div>

            <div class="ssoResult__card">
                <div class="ssoResult__cardIcon">💧</div>
                <div class="ssoResult__cardBody">
                    <div class="ssoResult__cardTitle">{translate('NJO_RESULT_HYDRATION_TITLE', 'Hidratación')}</div>
                    <div class="ssoResult__cardValue">{result.hydration}L</div>
                    <div class="ssoResult__cardDesc">
                        {translate('NJO_RESULT_HYDRATION_DESC', '{l}L de agua al día según tu actividad', { l: String(result.hydration) })}
                    </div>
                </div>
            </div>

            <div class="ssoResult__card">
                <div class="ssoResult__cardIcon">⚡</div>
                <div class="ssoResult__cardBody">
                    <div class="ssoResult__cardTitle">{translate('NJO_RESULT_WINDOW_TITLE', 'Ventana post-entreno')}</div>
                    <div class="ssoResult__cardDesc">
                        {translate('NJO_RESULT_WINDOW_DESC', 'Proteína + carbohidratos en los 30-45 min posteriores')}
                    </div>
                </div>
            </div>

            <div class="ssoResult__insight">
                <div class="ssoResult__insightTitle">{translate('NJO_RESULT_INSIGHT_TITLE', 'Tu punto clave')}</div>
                <p class="ssoResult__insightText">{result.sportTip}</p>
            </div>
        </div>

        <button
            class="ssoResult__cta"
            type="button"
            onclick={() => saveAndContinue()}
            disabled={saving}
        >
            {saving
                ? translate('SSO_CALCULATING', 'Calculando tu perfil...')
                : translate('NJO_SAVE_PROFILE', 'Activar seguimiento completo')}
        </button>

        <p class="ssoResult__poweredBy">{translate('SSO_POWERED_BY', 'powered by Caloo')}</p>
    {/if}
</section>

<style lang="scss">
    .ssoResult {
        min-height: 100vh;
        background: var(--backgroundMain);
        color: var(--textColor);
        display: grid;
        align-content: start;
        gap: 20px;
        padding: 32px 20px 40px;
        font-family: 'Montserrat', sans-serif;
    }

    .ssoResult__loading {
        display: grid;
        place-items: center;
        gap: 16px;
        padding-top: 40vh;
        text-align: center;
        font-size: 14px;
        color: var(--subtleText);
    }

    /* WOW counter ring */
    .ssoResult__wow {
        display: grid;
        justify-items: center;
        gap: 10px;
        margin-top: 8px;
    }

    .ssoResult__wowRing {
        width: 160px;
        height: 160px;
        border-radius: 999px;
        border: 4px solid var(--partner-primary, var(--primary));
        display: grid;
        place-items: center;
        flex-direction: column;
        gap: 2px;
        transition: box-shadow 0.4s ease;
    }

    .ssoResult__wowRing--done {
        box-shadow: 0 0 0 8px rgba(76, 227, 157, 0.1), 0 0 32px rgba(76, 227, 157, 0.18);
    }

    .ssoResult__wowNum {
        font-size: 34px;
        font-weight: 900;
        color: var(--black);
        letter-spacing: -0.02em;
        line-height: 1;
    }

    .ssoResult__wowUnit {
        font-size: 12px;
        font-weight: 700;
        color: var(--subtleText);
        letter-spacing: 0.02em;
    }

    .ssoResult__wowLabel {
        margin: 0;
        font-size: 13px;
        color: var(--subtleText);
        text-align: center;
    }

    .ssoResult__title {
        margin: 0;
        font-size: 20px;
        font-weight: 900;
        color: var(--black);
        letter-spacing: -0.02em;
    }

    /* Metric cards */
    .ssoResult__cards {
        display: grid;
        gap: 12px;
    }

    .ssoResult__card {
        display: flex;
        align-items: flex-start;
        gap: 14px;
        padding: 16px 18px;
        background: var(--white);
        border: 1px solid var(--border);
        border-radius: 16px;
        box-shadow: 0 1px 4px rgba(0,0,0,0.05);
    }

    .ssoResult__cardIcon {
        font-size: 24px;
        line-height: 1;
        flex-shrink: 0;
        padding-top: 2px;
    }

    .ssoResult__cardBody {
        display: grid;
        gap: 3px;
        min-width: 0;
    }

    .ssoResult__cardTitle {
        font-size: 11px;
        font-weight: 900;
        color: var(--subtleText);
        text-transform: uppercase;
        letter-spacing: 0.05em;
    }

    .ssoResult__cardValue {
        font-size: 22px;
        font-weight: 900;
        color: var(--black);
        letter-spacing: -0.02em;
        line-height: 1.1;
    }

    .ssoResult__cardDesc {
        font-size: 12px;
        color: var(--subtleText);
        line-height: 1.4;
    }

    .ssoResult__insight {
        padding: 16px 18px;
        background: rgba(76, 227, 157, 0.06);
        border: 1px solid rgba(76, 227, 157, 0.2);
        border-radius: 16px;
    }

    .ssoResult__insightTitle {
        font-size: 11px;
        font-weight: 900;
        color: var(--partner-primary, var(--primary));
        text-transform: uppercase;
        letter-spacing: 0.05em;
        margin-bottom: 6px;
    }

    .ssoResult__insightText {
        margin: 0;
        font-size: 13px;
        color: var(--textColor);
        line-height: 1.5;
        font-weight: 600;
    }

    /* CTA */
    .ssoResult__cta {
        width: 100%;
        padding: 16px 24px;
        border: none;
        border-radius: 14px;
        background: linear-gradient(180deg, #5aeaa0 0%, var(--partner-primary, var(--primary)) 100%);
        color: #12121f;
        font-size: 15px;
        font-weight: 900;
        font-family: inherit;
        cursor: pointer;
        letter-spacing: -0.01em;
        box-shadow: 0 4px 14px rgba(76, 227, 157, 0.25);
        transition: opacity 0.15s, transform 0.12s;
    }

    .ssoResult__cta:hover:not(:disabled) {
        opacity: 0.9;
        transform: translateY(-1px);
    }

    .ssoResult__cta:active:not(:disabled) {
        transform: scale(0.98);
    }

    .ssoResult__cta:disabled {
        opacity: 0.6;
        cursor: not-allowed;
    }

    .ssoResult__poweredBy {
        font-size: 11px;
        color: var(--subtleText);
        opacity: 0.5;
        text-align: center;
        letter-spacing: 0.04em;
    }
</style>
